// Moved from `src/hooks/mutation/useMutateToggleTeamConsent.ts` (now the app
// delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Three severances (ARCFE-921): both toasts, the two conditional Rematch
// `analyticsScribe` dispatches, and `invalidateCurrentUser()`.
//
// `invalidateCurrentUser` crosses as `onCurrentUserRefreshNeeded` rather than
// being reimplemented here, and that is deliberate: it invalidates GET_USER
// (workspace's) AND GET_PRODUCTS (COMMERCE's) behind one app-side try/catch.
// Reproducing only the workspace leg would silently drop a commerce
// invalidation; per delivery section 3 the foreign command belongs at the
// delegate, sequenced by app integration.
//
// The `useQueryUser` read the legacy hook made was for `adminId` in the
// analytics payload ONLY — the delegate reads it, so the package drops it.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS } from '../../contracts';
import { toggleTeamConsent } from '../api/teams';

export interface ToggleTeamConsentVariables {
  teamId: string;
  consentEnabled: boolean;
  customConsentText?: string | null;
  /** Whether consent toggle changed (vs just text) */
  consentToggleChanged?: boolean;
  /** Whether custom text changed */
  customTextChanged?: boolean;
}

export interface UseMutateToggleTeamConsentOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onCurrentUserRefreshNeeded?: () => void;
  /**
   * App-reaction port: fired where the success toast and the two conditional
   * Rematch dispatches fired, in that order. The conditionals live at the
   * delegate because they gate ANALYTICS, not data.
   */
  onConsentUpdated?: (variables: ToggleTeamConsentVariables) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onConsentUpdateError?: (error: Error) => void;
}

/** PATCH /teams/{team_id}/consent */
export function useMutateToggleTeamConsent(options?: UseMutateToggleTeamConsentOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['toggleTeamConsent'],
    mutationFn: async (input: ToggleTeamConsentVariables) => {
      await toggleTeamConsent({
        teamId: input.teamId,
        consentEnabled: input.consentEnabled,
        customConsentText: input.customConsentText,
      });
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: input.teamId }],
      });

      options?.onCurrentUserRefreshNeeded?.();

      options?.onConsentUpdated?.(input);
    },
    onError: (error) => {
      logger.error('Error updating consent settings:', error);
      options?.onConsentUpdateError?.(error);
    },
  });
}
